import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Keyboard } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

interface Shortcut {
  keys: string[];
  description: string;
  category: string;
}

const shortcuts: Shortcut[] = [
  { keys: ['H'], description: 'shortcut_hallway', category: 'navigation' },
  { keys: ['?'], description: 'shortcut_random', category: 'navigation' },
  { keys: ['Esc'], description: 'shortcut_back', category: 'navigation' },
  { keys: ['B'], description: 'shortcut_bookmark', category: 'rooms' },
  { keys: ['R'], description: 'shortcut_reading', category: 'accessibility' },
  { keys: ['/', 'Ctrl'], description: 'shortcut_search', category: 'navigation' },
  { keys: ['S'], description: 'shortcut_stats', category: 'navigation' },
  { keys: ['A'], description: 'shortcut_achievements', category: 'navigation' },
];

export const KeyboardShortcutsOverlay = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if typing
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) return;

      // ? key to toggle shortcuts overlay
      if ((e.key === '?' && e.shiftKey) || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }

      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) acc[shortcut.category] = [];
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, Shortcut[]>);

  const categoryLabels: Record<string, string> = {
    navigation: t('shortcut_cat_nav') || 'Navigation',
    rooms: t('shortcut_cat_rooms') || 'Rooms',
    accessibility: t('shortcut_cat_access') || 'Accessibility',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-background/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setIsOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="bg-card border border-border/30 rounded-lg shadow-2xl max-w-md w-full p-6 relative"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Keyboard className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-light text-foreground">
                  {t('keyboard_shortcuts') || 'Keyboard Shortcuts'}
                </h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-muted-foreground/50 hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Shortcuts List */}
            <div className="space-y-5">
              {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
                <div key={category}>
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground/50 mb-3">
                    {categoryLabels[category]}
                  </h3>
                  <div className="space-y-2">
                    {categoryShortcuts.map((shortcut, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between py-2 px-3 rounded bg-card/50"
                      >
                        <span className="text-sm text-foreground/80">
                          {t(shortcut.description) || shortcut.description}
                        </span>
                        <div className="flex items-center gap-1">
                          {shortcut.keys.map((key, keyIndex) => (
                            <span key={keyIndex}>
                              {keyIndex > 0 && (
                                <span className="text-muted-foreground/30 mx-1">+</span>
                              )}
                              <kbd className="px-2 py-1 text-xs bg-muted/30 border border-border/30 rounded font-mono">
                                {key}
                              </kbd>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer hint */}
            <p className="text-xs text-muted-foreground/40 text-center mt-6">
              {t('press_to_close') || 'Press'} <kbd className="px-1 py-0.5 bg-muted/20 rounded text-[10px]">?</kbd> {t('or') || 'or'} <kbd className="px-1 py-0.5 bg-muted/20 rounded text-[10px]">Esc</kbd> {t('to_close') || 'to close'}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default KeyboardShortcutsOverlay;
